import { useEffect, useRef } from 'react';

const MAX_RETRIES = 3;

/**
 * Polls fn() on a recurring schedule.
 *
 * Guarantees:
 *  - In-flight guard: next tick is never fired while a request is still running.
 *    If the previous call hasn't resolved after 1 s, it retries the guard check
 *    instead of stacking a new request on top.
 *  - Timeout: pass { timeout: ms } in your axios config — ECONNABORTED is treated
 *    as a retriable error.
 *  - Exponential backoff on consecutive failures: 2 s → 4 s → 8 s (capped at 30 s).
 *  - After MAX_RETRIES consecutive failures the poller resets and resumes at the
 *    normal interval rather than giving up entirely.
 *
 * @param {() => Promise<any>} fn        Must return a Promise (or usePoll wraps it).
 * @param {number}             interval  ms between successful polls.  Default 5000.
 * @param {any[]}              deps      Re-starts the poller when any dep changes.
 *
 * @example
 *   usePoll(() => fetchTokens(dispatch, actionType), 10_000, [page]);
 */
export function usePoll(fn, interval = 5000, deps = []) {
  const fnRef    = useRef(fn);
  const timerRef = useRef(null);
  const inFlight = useRef(false);
  const retries  = useRef(0);

  // Keep fnRef current on every render without restarting the poller
  useEffect(() => { fnRef.current = fn; });

  useEffect(() => {
    inFlight.current = false;
    retries.current  = 0;
    if (timerRef.current) clearTimeout(timerRef.current);

    function tick() {
      if (inFlight.current) {
        // Previous request still running — check again in 1 s
        timerRef.current = setTimeout(tick, 1000);
        return;
      }

      inFlight.current = true;

      Promise.resolve(fnRef.current())
        .then(() => {
          retries.current  = 0;
          inFlight.current = false;
          timerRef.current = setTimeout(tick, interval);
        })
        .catch((err) => {
          inFlight.current = false;
          const isTimeout = err?.code === 'ECONNABORTED';

          if (retries.current < MAX_RETRIES) {
            retries.current += 1;
            const delay = Math.min(2000 * Math.pow(2, retries.current - 1), 30_000);
            console.warn(
              `[usePoll] ${isTimeout ? 'timeout' : 'error'} — ` +
              `retry ${retries.current}/${MAX_RETRIES} in ${delay} ms`
            );
            timerRef.current = setTimeout(tick, delay);
          } else {
            // Max retries hit — reset and resume normal cadence
            retries.current  = 0;
            timerRef.current = setTimeout(tick, interval);
          }
        });
    }

    tick(); // fire immediately on mount / dep change

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      inFlight.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
